import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Play, Pause, SkipForward, Volume2, VolumeX, Music } from 'lucide-react';
import axios from 'axios';

const API_V2 = process.env.REACT_APP_BACKEND_URL + '/api/v2';

const MiniPlayer = () => {
  const audioRef = useRef(null);
  const [tracks, setTracks] = useState([]);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);

  const fetchTracks = useCallback(async () => {
    try {
      const res = await axios.get(`${API_V2}/music/tracks`, { withCredentials: true });
      setTracks((res.data.tracks || []).filter(t => t.enabled !== false));
    } catch {}
  }, []);

  useEffect(() => { fetchTracks(); }, [fetchTracks]);

  const blobUrlRef = useRef(null);

  const playTrack = async (track) => {
    const audio = audioRef.current;
    if (!audio || !track) return;

    if (currentTrack?.track_id === track.track_id) {
      if (audio.paused) {
        audio.play().catch(e => console.error('MiniPlayer resume:', e));
      } else {
        audio.pause();
      }
      return;
    }

    setCurrentTrack(track);
    setProgress(0);

    // Clean up previous blob URL
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    try {
      const url = `${API_V2}/music/tracks/${track.track_id}/stream`;
      const response = await fetch(url);
      if (!response.ok) return;
      const blob = await response.blob();
      const audioBlob = new Blob([blob], { type: 'audio/mpeg' });
      const blobUrl = URL.createObjectURL(audioBlob);
      blobUrlRef.current = blobUrl;
      audio.src = blobUrl;
      audio.play().catch(() => {});
    } catch (err) {
      console.error('MiniPlayer fetch error:', err);
    }
  };

  const playRandom = () => {
    if (tracks.length === 0) return;
    const others = tracks.filter(t => t.track_id !== currentTrack?.track_id);
    const pick = others.length > 0 ? others[Math.floor(Math.random() * others.length)] : tracks[0];
    playTrack(pick);
  };

  const pct = duration > 0 ? (progress / duration) * 100 : 0;
  if (tracks.length === 0) return null;

  return (
    <div className="flex items-center gap-2 bg-[#0a0a0a] border border-[#003B00] px-3 py-1.5 rounded text-xs font-mono" data-testid="mini-player">
      <audio
        ref={audioRef}
        preload="auto"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
        onTimeUpdate={() => setProgress(audioRef.current?.currentTime || 0)}
        onEnded={playRandom}
        onError={() => setIsPlaying(false)}
      />
      <Music className={`h-3.5 w-3.5 shrink-0 ${isPlaying ? 'text-[#00FF41] animate-pulse' : 'text-[#333]'}`} />
      <span className="text-[#00FF41] truncate max-w-[120px]" data-testid="mini-player-title">
        {currentTrack?.title || 'BitBot Radio'}
      </span>
      <div className="flex-1 h-1 bg-[#111] rounded-full overflow-hidden min-w-[40px] max-w-[80px]">
        <div className="h-full bg-[#00FF41] transition-all" style={{ width: `${pct}%` }} />
      </div>
      <Button variant="ghost" size="icon" className="h-6 w-6 text-[#00FF41] p-0" data-testid="mini-play-btn" onClick={() => {
        if (!currentTrack && tracks.length > 0) playRandom();
        else if (currentTrack) playTrack(currentTrack);
      }}>
        {isPlaying ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
      </Button>
      <Button variant="ghost" size="icon" className="h-6 w-6 text-[#008F11] p-0" data-testid="mini-skip-btn" onClick={playRandom}>
        <SkipForward className="h-3 w-3" />
      </Button>
      <Button variant="ghost" size="icon" className="h-6 w-6 p-0" data-testid="mini-mute-btn" onClick={() => {
        setMuted(m => { const n = !m; if (audioRef.current) audioRef.current.volume = n ? 0 : 0.5; return n; });
      }}>
        {muted ? <VolumeX className="h-3 w-3 text-[#333]" /> : <Volume2 className="h-3 w-3 text-[#008F11]" />}
      </Button>
    </div>
  );
};

export default MiniPlayer;
